import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import CosmeticItemCard from './components/CosmeticItemCard';
import { SkeletonCard, EmptyState } from 'components/SkeletonLoader';
import { useAuth } from '../../contexts/AuthContext';
import { logError } from '../../utils/logError';


const TABS = [
  { id: 'ALL', label: 'All' },
  { id: 'PROFILE_THEME', label: 'Profile Theme' },
  { id: 'UI_THEME', label: 'UI Theme' },
  { id: 'AVATAR_FRAME', label: 'Avatar Frame' },
  { id: 'FACTION_BANNER', label: 'Faction Banner' },
  { id: 'CHART_STYLE', label: 'Chart Style' },
];

const Toast = ({ toasts, onRemove }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts?.map((t) => (
      <div
        key={t?.id}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium animate-fade-in ${
          t?.type === 'success' ?'bg-success/10 border-success/30 text-success' :'bg-error/10 border-error/30 text-error'
        }`}
      >
        <Icon name={t?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={16} />
        {t?.message}
        <button onClick={() => onRemove?.(t?.id)} className="ml-2 opacity-60 hover:opacity-100">
          <Icon name="X" size={12} />
        </button>
      </div>
    ))}
  </div>
);

const MarketplacePage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [cosmetics, setCosmetics] = useState([]);
  const [ownedIds, setOwnedIds] = useState(new Set());
  const [equippedIds, setEquippedIds] = useState(new Set());
  const [playerCash, setPlayerCash] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const { user } = useAuth();

  const addToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev?.filter((t) => t?.id !== id)), 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      setCurrentUser(user);

      // Fetch all active cosmetics
      const { data: cosmeticsData, error: cosmeticsError } = await supabase
        ?.from('cosmetics')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('price', { ascending: true });

      if (cosmeticsError) throw cosmeticsError;
      setCosmetics(cosmeticsData || []);

      if (user) {
        // Fetch player cash
        const { data: statsData } = await supabase
          ?.from('player_stats')
          ?.select('cash')
          ?.eq('user_id', user?.id)
          ?.single();
        setPlayerCash(statsData?.cash || 0);

        // Fetch owned cosmetics
        const { data: ownedData } = await supabase
          ?.from('player_cosmetics')
          ?.select('cosmetic_id, is_equipped')
          ?.eq('user_id', user?.id);

        const owned = new Set(ownedData?.map((o) => o?.cosmetic_id) || []);
        const equipped = new Set(
          ownedData?.filter((o) => o?.is_equipped)?.map((o) => o?.cosmetic_id) || []
        );
        setOwnedIds(owned);
        setEquippedIds(equipped);
      }
    } catch (err) {
      addToast(err?.message || 'Failed to load marketplace', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePurchase = async (cosmeticId, price) => {
    if (!currentUser) {
      addToast('Please sign in to purchase items', 'error');
      return;
    }
    if (playerCash < price) {
      addToast('Insufficient funds to purchase this item', 'error');
      return;
    }

    // Optimistic UI: immediately add to owned with pending state
    const prevOwnedIds = new Set(ownedIds);
    const prevCash = playerCash;
    setOwnedIds(prev => new Set([...prev, cosmeticId]));
    setPlayerCash(prev => prev - price);
    setPurchasing(cosmeticId);

    try {
      const { data, error } = await supabase?.rpc('purchase_cosmetic', {
        p_cosmetic_id: cosmeticId,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Purchase failed');

      addToast('Item purchased! Added to your inventory.', 'success');
    } catch (err) {
      // Rollback
      setOwnedIds(prevOwnedIds);
      setPlayerCash(prevCash);
      addToast(err?.message || 'Purchase failed', 'error');
      logError({ error: err, componentName: 'Marketplace', actionName: 'handlePurchase', screenName: 'marketplace', userId: user?.id || currentUser?.id, context: { cosmeticId, price } });
    } finally {
      setPurchasing(null);
    }
  };

  const handleEquip = async (cosmeticId) => {
    setPurchasing(cosmeticId);
    try {
      const { data, error } = await supabase?.rpc('equip_cosmetic', {
        p_cosmetic_id: cosmeticId,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Equip failed');

      // Find skin_type of equipped item to unequip others of same type
      const item = cosmetics?.find((c) => c?.id === cosmeticId);
      setEquippedIds((prev) => {
        const next = new Set(prev);
        // Remove others of same skin_type
        cosmetics
          ?.filter((c) => c?.skin_type === item?.skin_type && c?.id !== cosmeticId)
          ?.forEach((c) => next?.delete(c?.id));
        next?.add(cosmeticId);
        return next;
      });
      addToast('Cosmetic equipped!', 'success');
    } catch (err) {
      addToast(err?.message || 'Equip failed', 'error');
    } finally {
      setPurchasing(null);
    }
  };

  const handleUnequip = async (cosmeticId) => {
    setPurchasing(cosmeticId);
    try {
      const { data, error } = await supabase?.rpc('unequip_cosmetic', {
        p_cosmetic_id: cosmeticId,
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Unequip failed');

      setEquippedIds((prev) => {
        const next = new Set(prev);
        next?.delete(cosmeticId);
        return next;
      });
      addToast('Cosmetic unequipped.', 'success');
    } catch (err) {
      addToast(err?.message || 'Unequip failed', 'error');
    } finally {
      setPurchasing(null);
    }
  };

  const filtered = cosmetics?.filter((item) => {
    const matchTab = activeTab === 'ALL' || item?.skin_type === activeTab;
    const matchSearch = item?.cosmetic_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchTab && matchSearch;
  });

  const tabCounts = TABS?.reduce((acc, tab) => {
    acc[tab.id] =
      tab?.id === 'ALL'
        ? cosmetics?.length
        : cosmetics?.filter((c) => c?.skin_type === tab?.id)?.length;
    return acc;
  }, {});

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
      />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-h2 font-heading text-foreground flex items-center gap-3">
                <Icon name="ShoppingBag" size={28} color="var(--color-primary)" />
                Marketplace
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Cosmetic items only — no pay-to-win</p>
            </div>
            <div className="flex items-center gap-3">
              {currentUser && (
                <div className="flex items-center gap-2 px-4 py-2 bg-warning/10 border border-warning/30 rounded-lg">
                  <Icon name="Coins" size={16} color="var(--color-warning)" />
                  <span className="font-mono font-bold text-warning">
                    {playerCash?.toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground">cash</span>
                </div>
              )}
            </div>
          </div>

          {/* Fair Play Notice */}
          <div className="p-3 mb-6 rounded-lg bg-success/5 border border-success/20 flex items-center gap-3">
            <Icon name="ShieldCheck" size={16} color="var(--color-success)" />
            <p className="text-xs text-success">
              <strong>Fair Play Guarantee:</strong> All items are cosmetic only. No XP boosts, crime
              success boosts, leverage boosts, or economic advantages are sold.
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-5">
            <Icon
              name="Search"
              size={14}
              color="var(--color-muted-foreground)"
              className="absolute left-3 top-1/2 -translate-y-1/2"
            />
            <input
              type="text"
              placeholder="Search cosmetics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full bg-surface border border-border rounded-lg pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
            {TABS?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  activeTab === tab?.id
                    ? 'bg-primary text-zinc-950' :'bg-surface border border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab?.label}
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab?.id ? 'bg-zinc-950/20' : 'bg-primary/10 text-primary'
                }`}>
                  {tabCounts?.[tab?.id] || 0}
                </span>
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {Array(12)?.fill(0)?.map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : filtered?.length === 0 ? (
            <EmptyState
              icon={<Icon name="ShoppingBag" size={28} color="var(--color-primary)" />}
              title="No cosmetics available"
              description={searchQuery ? `No items match "${searchQuery}". Try a different search term.` : 'No items in this category yet. Check back soon!'}
              {...(searchQuery && { action: () => setSearchQuery(''), actionLabel: 'Clear Search' })}
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filtered?.map((item) => (
                <CosmeticItemCard
                  key={item?.id}
                  item={item}
                  isOwned={ownedIds?.has(item?.id)}
                  isEquipped={equippedIds?.has(item?.id)}
                  onPurchase={handlePurchase}
                  onEquip={handleEquip}
                  onUnequip={handleUnequip}
                  purchasing={purchasing}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default MarketplacePage;
